import { useCallback } from 'react';
import type { ExportFormat, MarkdownDocument } from '@markbird/shared';
import { useStore } from '../store/useStore';
import { askNotice } from '../lib/dialogs';
import { t } from '../i18n';

export interface ExportApi {
  exportAs: (format: ExportFormat) => Promise<void>;
}

/**
 * Export handlers bound to the active document. All formats flow through the
 * one shared render pipeline (see `lib/export`), so exported output matches the
 * live preview.
 *
 * `lib/export` is loaded on first use, not at module scope. It pulls in the
 * DOCX and PDF writers, and this hook lives in the always-mounted toolbar — a
 * static import put that whole payload in the initial bundle for every visitor,
 * including the ones who never export anything.
 */
export function useExport(): ExportApi {
  const exportAs = useCallback(async (format: ExportFormat) => {
    const { id, title, content } = useStore.getState().activeDoc;
    const doc: MarkdownDocument = {
      id,
      title,
      content,
      createdAt: 0,
      updatedAt: 0,
      ownerId: null,
    };
    try {
      const { exportDocument } = await import('../lib/export');
      await exportDocument(doc, format);
    } catch (err) {
      await askNotice({
        title: t('export.failed'),
        body: err instanceof Error ? err.message : String(err),
        confirmLabel: t('common.close'),
      });
    }
  }, []);

  return { exportAs };
}
